/**
 * Destination helpers for Storage Explorer batch export.
 *
 * Resolves a single write target (Electron folder, browser directory picker,
 * or a ZIP download fallback) so every encoded file can be saved without a
 * per-file save dialog.
 */

import JSZip from "jszip";
import { saveAs } from "file-saver";

/**
 * @typedef {object} BatchExportDestination
 * @property {'electron'|'directory'|'zip'} type
 * @property {string} [folderPath]
 * @property {FileSystemDirectoryHandle} [dirHandle]
 * @property {Array<{name: string, blob: Blob}>} [files]
 * @property {Set<string>} usedNames
 */

/**
 * Returns a filename that does not collide with names already used in this run.
 * e.g. name.srt, name_2.srt, name_3.srt
 *
 * @param {string} fileName
 * @param {Set<string>} usedNames
 * @returns {string}
 */
export function uniquifyFileName(fileName, usedNames) {
    if (!usedNames.has(fileName)) {
        return fileName;
    }

    const lastDot = fileName.lastIndexOf(".");
    const base = lastDot === -1 ? fileName : fileName.slice(0, lastDot);
    const ext = lastDot === -1 ? "" : fileName.slice(lastDot);

    let n = 2;
    let candidate;
    do {
        candidate = `${base}_${n}${ext}`;
        n++;
    } while (usedNames.has(candidate));

    return candidate;
}

/**
 * Asks the user for a destination folder (or falls back to collecting files
 * for a single ZIP download when no folder API is available).
 *
 * @param {object} options
 * @param {string} options.title - Native folder-picker title (Electron).
 * @param {boolean} options.isElectron
 * @returns {Promise<BatchExportDestination|null>} null if the user cancelled.
 */
export async function selectBatchExportDestination({ title, isElectron }) {
    if (isElectron && window.ipcRenderer) {
        const result = await window.ipcRenderer.invoke("selectFolder", {
            title,
            properties: ["openDirectory", "createDirectory"],
        });
        if (!result || result.canceled || !result.filePaths?.[0]) {
            return null;
        }
        return {
            type: "electron",
            folderPath: result.filePaths[0],
            usedNames: new Set(),
        };
    }

    if (typeof window.showDirectoryPicker === "function") {
        try {
            const dirHandle = await window.showDirectoryPicker({ mode: "readwrite" });
            return {
                type: "directory",
                dirHandle,
                usedNames: new Set(),
            };
        } catch (err) {
            if (err && (err.name === "AbortError" || err.name === "NotAllowedError")) {
                return null;
            }
            // Fall through to ZIP if the picker is present but unusable.
        }
    }

    return {
        type: "zip",
        files: [],
        usedNames: new Set(),
    };
}

/**
 * Writes one encoded file into the chosen destination.
 *
 * @param {BatchExportDestination} destination
 * @param {string} fileName
 * @param {Blob|ArrayBuffer|Uint8Array} fileBlob
 * @returns {Promise<string>} The uniquified filename that was written.
 */
export async function writeBatchExportFile(destination, fileName, fileBlob) {
    const uniqueName = uniquifyFileName(fileName, destination.usedNames);
    destination.usedNames.add(uniqueName);
    const blob = fileBlob instanceof Blob ? fileBlob : new Blob([fileBlob]);

    if (destination.type === "electron") {
        const filePath = window.path.join(destination.folderPath, uniqueName);
        const fileArrayBuffer = await blob.arrayBuffer();
        await window.fs.writeFile(filePath, new Uint8Array(fileArrayBuffer));
        return uniqueName;
    }

    if (destination.type === "directory") {
        const fileHandle = await destination.dirHandle.getFileHandle(uniqueName, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        return uniqueName;
    }

    destination.files.push({ name: uniqueName, blob });
    return uniqueName;
}

/**
 * Finishes a ZIP-fallback export with a single save dialog.
 * No-op for folder destinations, or when nothing was collected.
 *
 * @param {BatchExportDestination} destination
 * @param {string} zipFileName
 * @returns {Promise<void>}
 */
export async function finalizeBatchExportDestination(destination, zipFileName) {
    if (destination.type !== "zip" || !destination.files || destination.files.length === 0) {
        return;
    }

    const zip = new JSZip();
    for (const file of destination.files) {
        zip.file(file.name, file.blob);
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    saveAs(zipBlob, zipFileName);
}
